import React, { useState, useEffect } from 'react';
import { X, Loader2, Users, CheckCircle2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client.ts';
import { IdeaItem } from './IdeasFilterTable.tsx';

interface BulkAssignModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIdeas: IdeaItem[];
  onSuccess?: () => void;
}

export function BulkAssignModal({ isOpen, onClose, selectedIdeas, onSuccess }: BulkAssignModalProps) {
  const queryClient = useQueryClient();
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      const existingIds = new Set<string>();
      selectedIdeas.forEach(idea => {
        idea.assignees?.forEach(a => {
          if (a.userId) existingIds.add(a.userId);
        });
      });
      setSelectedAssignees(Array.from(existingIds));
    } else {
      setSelectedAssignees([]);
    }
  }, [isOpen, selectedIdeas]);

  const ideaIds = selectedIdeas.map(idea => idea.id);
  const ideaIdsString = ideaIds.join(',');

  const { data: eligibleMembers, isLoading: membersLoading } = useQuery({
    queryKey: ['bulkEligibleAssignees', ideaIdsString],
    queryFn: async () => {
      if (!ideaIdsString) return [];
      const res = await apiClient.get(`/admin/ideas/bulk-eligible-assignees?ids=${ideaIdsString}`);
      return res.data.data;
    },
    enabled: isOpen && ideaIds.length > 0,
  });

  const assignMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post('/admin/ideas/bulk-assign-committee', {
        ideaIds,
        assigneeIds: selectedAssignees,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminStats'] });
      queryClient.invalidateQueries({ queryKey: ['headIdeas'] });
      if (onSuccess) onSuccess();
      onClose();
    },
  });

  const toggleAssignee = (userId: string) => {
    setSelectedAssignees(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />

      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden relative z-10 flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white sticky top-0">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Bulk Assign Ideas</h2>
            <p className="text-slate-500 text-sm">Assigning {selectedIdeas.length} ideas</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">
          {/* Ideas Preview Table */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Selected Ideas</h3>
            <div className="border border-slate-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 font-semibold text-slate-600">Ticket ID</th>
                    <th className="px-4 py-2 font-semibold text-slate-600">Title</th>
                    <th className="px-4 py-2 font-semibold text-slate-600">Department</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {selectedIdeas.map(idea => (
                    <tr key={idea.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2 text-slate-500 font-mono text-xs uppercase">
                        CG{idea.id.substring(0, 7)}
                      </td>
                      <td className="px-4 py-2 text-slate-800 font-medium truncate max-w-[200px]" title={idea.title}>
                        {idea.title}
                      </td>
                      <td className="px-4 py-2 text-slate-600 uppercase text-xs">
                        {idea.department}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Committee Member Selection */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Select Committee Members (Multiple)</h3>
            {membersLoading ? (
              <div className="flex items-center justify-center py-8 bg-slate-50 rounded-lg border border-slate-100">
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
              </div>
            ) : eligibleMembers?.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg border border-slate-100 text-slate-500 text-sm">
                No eligible committee members found for these departments.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-2">
                {eligibleMembers?.map((member: any) => {
                  const isSelected = selectedAssignees.includes(member.id);
                  return (
                    <div
                      key={member.id}
                      onClick={() => toggleAssignee(member.id)}
                      className={`flex items-start p-3 rounded-lg border cursor-pointer transition-all ${isSelected
                        ? 'border-blue-500 bg-blue-50 shadow-sm'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                        }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <p className={`text-sm font-semibold truncate ${isSelected ? 'text-blue-800' : 'text-slate-800'}`}>
                            {member.name}
                          </p>
                          {isSelected && <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 ml-2" />}
                        </div>
                        <p className={`text-xs truncate mt-0.5 ${isSelected ? 'text-blue-600/80' : 'text-slate-500'}`}>
                          {member.email}
                        </p>
                        <p className={`text-[10px] uppercase font-bold mt-1.5 ${isSelected ? 'text-blue-500' : 'text-slate-400'}`}>
                          {member.department || 'All Departments'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end space-x-3 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (window.confirm(`Are you sure you want to assign these ${selectedIdeas.length} ideas to ${selectedAssignees.length} committee members?`)) {
                assignMutation.mutate();
              }
            }}
            disabled={assignMutation.isPending}
            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-semibold flex items-center space-x-2 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {assignMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Users className="w-4 h-4" />
            )}
            <span>Confirm Assignment</span>
          </button>
        </div>
      </div>
    </div>
  );
}
